import React from 'react';
import { P3Campaign, ProductFulfillment } from 'models/interfaces';
import { campaignHasFulfillment, readOnlyConfiguration } from 'models/constants/index';
import { CONSTANTS } from '@tpco/tpco-saturn-types';
import { P3Configuration as IP3Configuration } from 'models/interfaces/P3Configurations';
import P3Configuration from 'models/P3Configuration';
import debounce from 'lodash/debounce';
import { iso8601 } from 'util/dateUtils';
import ConfirmModal from '../Modal/ConfirmModal';
import { configurationValidPoints } from 'util/p3Validation';
import P3CampaignEditForm from './P3CampaignEditForm';
import { nameValidation } from './P3CampaignEditFormConfiguration';
import { canBeEdited } from 'util/p3Validation';
import './P3CampaignEdit.scss';

interface P3CampaignEditProps {
  companyName: string;
  startDate: string;
  endDate: string;
  campaignId: string;
  productFulfillment?: ProductFulfillment;
  campaignStatusId: number;
  onCampaignChange(p3Campaign: any): void;
  onConfigNameChange(name: string): void;
  onRemoveConfiguration(configurationId: string, campaignId: string): void;
  p3Configurations: IP3Configuration[];
  p3Campaign: P3Campaign;
  tgramsId: string;
}

interface P3CampaignEditState {
  rankPoints: number;
  coverageAreas: number;
  previewAds: number;
  tgramsId: string;
  campaignId: string;
  templateOptionId: number;
  p3Configurations: IP3Configuration[];
  configurationName: string;
  start_date: string;
  end_date: string;
  // modal data
  showConfirmRemoveModal: boolean;
  confirmModalDiv: any;
  // state for managing the template selected
  configurationId?: string;
  p3Configuration?: IP3Configuration;
  hasSavedConfiguration: boolean;
  initialConfigurationId: string;
}

class P3CampaignEdit extends React.Component<P3CampaignEditProps, P3CampaignEditState> {
  constructor(props: P3CampaignEditProps) {
    super(props);

    const {
      entitled_rank_points,
      entitled_preview_ads,
      entitled_coverage_areas,

      p3_configuration_id
    } = props.p3Campaign;

    const { p3Campaign, campaignId } = this.props;

    let templateOptionId = 1;

    let configurationName = '';
    let p3Configuration;
    let initialConfigurationId = '';

    // If this campaign has already been configured, set the configuration & name
    if (p3Campaign.p3_configuration) {
      configurationName = p3Campaign.p3_configuration.name;
      initialConfigurationId = p3Campaign.p3_configuration_id;
      p3Configuration = p3Campaign.p3_configuration;
      templateOptionId = 2;
    } else {
      p3Configuration = undefined;
    }

    this.state = {
      tgramsId: this.props.tgramsId,
      campaignId: campaignId,
      start_date: props.startDate,
      end_date: props.endDate,
      rankPoints: entitled_rank_points || 0,
      coverageAreas: entitled_coverage_areas || 0,
      previewAds: entitled_preview_ads || 0,
      configurationId: p3_configuration_id,
      p3Configurations: props.p3Configurations,
      templateOptionId,
      configurationName,
      showConfirmRemoveModal: false,
      hasSavedConfiguration: !!this.props.p3Campaign.p3_configuration_id,
      initialConfigurationId,
      p3Configuration,
      confirmModalDiv: document.createElement('div')
    };
    this.onClickRankButton = this.onClickRankButton.bind(this);
  }

  /**
   * Triggered when the p3 rankPoints change, this function is
   * responsible for setting the internal entitlements' state and
   * calling the local onCampaignChange
   */
  onRankPointsChange = ({ value }: { value: any }) => {
    if (!value) {
      this.setState({ rankPoints: 0 });
    } else {
      this.setState({ rankPoints: parseInt(value, 10) }, this.onCampaignChange);
    }
  };

  /**
   * This function should be called whenever the properties of the P3Campaign that
   * this component represents change. It is responsible for building a p3 campaign
   * object from props and state, and sending that object to its parent via the
   * onCampaignChange prop.
   */
  onCampaignChange = () => {
    // build ps campaign object from state and props
    const p3 = this.buildP3Campaign();
    // write campaign module to cache
    this.props.onCampaignChange(p3);
  };

  /**
   * Responsible for building a P3Campaign object from props and state
   */
  buildP3Campaign = () => {
    // construct P3Campaign object
    const { startDate, endDate, campaignId, companyName, tgramsId } = this.props;

    const {
      rankPoints,
      coverageAreas,
      previewAds,
      configurationName,
      configurationId,
      p3Configuration,
      hasSavedConfiguration
    } = this.state;

    const p3: P3Campaign = {
      p3_campaign_id: campaignId,
      tgrams_id: tgramsId,
      p3_configuration_id: configurationId || '',
      name: configurationName,
      entitled_rank_points: rankPoints,
      entitled_coverage_areas: coverageAreas,
      entitled_preview_ads: previewAds,

      start_date: iso8601(startDate),
      end_date: iso8601(endDate),
      advertiser: {
        name: companyName,
        tgrams_id: tgramsId,
        pvsCampaigns: [],
        thomasCampaigns: [],
        p3Campaigns: []
      }
    };

    console.log('Rebuilding p3. this.state.p3Configuration: ', p3Configuration);
    // set name on p3Configuration object to configurationName so it saves correctly
    if (p3Configuration && p3Configuration.p3_configuration_id != '') {
      console.log('p3 exist and it is valid', p3Configuration);
      p3Configuration.name = configurationName;
      p3.p3_configuration = p3Configuration;
      p3.p3_configuration.regions = [...p3Configuration.regions];
      p3.p3_configuration.categories = [...p3Configuration.categories];
      p3.p3_configuration.tgrams_id = tgramsId;
    } else {
      // need to set a default in order to prevent the cache from blowing up
      p3.p3_configuration = new P3Configuration().makeP3ConfigurationInput();
      // set description, start & end date b/c they're optional by default
      p3.p3_configuration.tgrams_id = tgramsId;
      p3.p3_configuration.description = '';
      p3.p3_configuration.start_date = '';
      p3.p3_configuration.end_date = '';
    }

    return p3;
  };

  /**
   * This lifecycle method will check this components' props for changes
   * that affect the P3Campaign object this component represents and will
   * trigger a local onCampaignChange if any are found.
   */
  componentDidUpdate = (prevProps: P3CampaignEditProps) => {
    let campaignHasChanged = false;

    // when the following props change, we need to rebuild the P3Campaign
    // and inform the parent
    if (this.props.startDate !== prevProps.startDate) {
      campaignHasChanged = true;
    } else if (this.props.endDate !== prevProps.endDate) {
      campaignHasChanged = true;
    }

    if (campaignHasChanged) {
      this.onCampaignChange();
    }
  };

  onClickRankButton(event: any) {
    event.preventDefault();
    let path = `${CONSTANTS.RANKALYZER_BASE_PATH}?tgrams_id=${this.state.tgramsId}&campaign_id=${
      this.state.campaignId
    }`;
    window.location.href = path;
  }

  toggleConfirmRemoveModal = () => {
    // open confirmation modal
    this.setState({
      showConfirmRemoveModal: !this.state.showConfirmRemoveModal
    });
  };

  setRankPoints = (value: number) => {
    if (campaignHasFulfillment(this.props.productFulfillment)) {
      return this.state.rankPoints;
    } else {
      return value;
    }
  };

  onTemplateOptionChange = ({ value }: any) => {
    console.log('onTemplateOptionChange: ', value);
    // if you select the -- Select -- option
    if (!value) {
      this.setState(
        {
          templateOptionId: value,
          configurationName: '',
          configurationId: '',
          hasSavedConfiguration: false, // we're resetting the saved config
          rankPoints: this.setRankPoints(0),
          previewAds: 0,
          coverageAreas: 0
        },
        this.onCampaignChange
      );
      return;
    }

    // if the template is create new then wipe the configuration name and the selected p3_configuration_id
    const templateOptionId = parseInt(value, 10);
    if (templateOptionId === 1) {
      this.setState({
        templateOptionId,
        configurationName: '',
        configurationId: '',
        rankPoints: this.setRankPoints(0),
        previewAds: 0,
        coverageAreas: 0
      });
    } else {
      this.setState(
        {
          templateOptionId
        },
        this.onCampaignChange
      );
    }
  };

  applyTemplate = ({ value }: any) => {
    // if you select the -- Select -- option
    if (!value) {
      this.setState(
        {
          p3Configuration: undefined,
          hasSavedConfiguration: false,
          configurationId: '',
          configurationName: ''
        },
        this.onCampaignChange
      );
      return;
    }

    const { p3Configurations } = this.state;
    //const tmconfig = p3Configurations.find(x => x.p3_configuration_id === value)
    const p3Configuration = p3Configurations.find(x => x.p3_configuration_id === value); //{...tmconfig} as IP3Configuration;

    if (!p3Configuration) {
      throw new Error(`Failed to find p3Configuration in p3Configurations with value ${value}`);
    }

    const { companyName, onConfigNameChange } = this.props;

    onConfigNameChange(p3Configuration.name);

    const name = `${companyName} - ${p3Configuration.name}`;

    this.setState(
      {
        p3Configuration: { ...p3Configuration },
        // hasSavedConfiguration: true,
        configurationId: p3Configuration.p3_configuration_id,
        configurationName: name,
        rankPoints: this.setRankPoints(configurationValidPoints(p3Configuration)) // don't override rankPoints for campaign if this campaign has a product fulfillment
      },
      this.onCampaignChange
    );
  };

  onConfigurationNameChange = debounce(({ value }: any) => {
    // notify parent of change
    this.props.onConfigNameChange(value);

    // set state
    this.setState(
      {
        configurationName: value
      },
      this.onCampaignChange
    );
  }, 500);

  onRemoveConfig = async () => {
    // reset state
    this.setState(
      {
        p3Configuration: undefined,
        hasSavedConfiguration: false,
        configurationId: '',
        configurationName: ''
      },
      this.onCampaignChange
    );
    this.onTemplateOptionChange({ value: '' });

    // actually delete records if this camapaign has been saved
    if (this.state.configurationId) {
      this.props.onRemoveConfiguration(this.state.configurationId, this.state.campaignId);
    }

    // close the modal
    this.setState({ showConfirmRemoveModal: false });
  };

  areRankPointsEditable = () => {
    const { productFulfillment } = this.props;

    if (campaignHasFulfillment(productFulfillment)) {
      return false;
    }

    return true;
  };

  isNameEditable = () => {
    const { campaignStatusId, startDate, productFulfillment } = this.props;

    if (
      !canBeEdited(
        campaignStatusId,
        startDate,
        productFulfillment ? productFulfillment.opportunity_id : ''
      )
    ) {
      return false;
    }

    return true;
  };

  isCampaignEditable = () => {
    const { campaignStatusId, startDate, productFulfillment } = this.props;
    return canBeEdited(
      campaignStatusId,
      startDate,
      productFulfillment ? productFulfillment.opportunity_id : '',
      productFulfillment ? productFulfillment.order_source : ''
    );
  };

  canEditTemplate = () => {
    const { campaignStatusId, startDate, productFulfillment } = this.props;

    return canBeEdited(
      campaignStatusId,
      startDate,
      productFulfillment ? productFulfillment.opportunity_id : ''
    );
  };

  componentDidMount = () => {
    if (this.props.p3Campaign) {
      nameValidation(this.state.configurationName);
    }

    // build campaign module and notify parent
    this.onCampaignChange();
  };

  componentWillUnmount = () => {
    // clear campaignModule cache
    this.props.onCampaignChange(null);
  };

  static getDerivedStateFromProps(nextProps: P3CampaignEditProps, prevState: P3CampaignEditState) {
    /**
     * DON'T HATE ME. PURPOSE FOR THIS ANTI-PATTERN:
     * We need a way to tell if a re-fetch happened and this campaign's configuration changed
     */

    // If this is a new p3Campaign or a completely unconfigured one, ignore
    if (!nextProps.p3Campaign || !nextProps.p3Campaign.p3_configuration_id) {
      return null;
    }

    // This boolean represents a change in this campaign's configuration.
    // We may need to update this component's state to reflect a new saved configuration.
    const propsConfigIdMatchesInitial =
      nextProps.p3Campaign.p3_configuration_id === prevState.initialConfigurationId;

    // This boolean means that the user has selected Create new or Apply templates
    // from the "Template Configuration" dropdown
    const configurationOptionIsSet = !!prevState.templateOptionId;

    // If the props config id changed, and we don't already have a saved configuration,
    // AND the configuration dropdown is already set, then we need to update our state
    if (
      !propsConfigIdMatchesInitial &&
      !prevState.hasSavedConfiguration &&
      configurationOptionIsSet
    ) {
      console.log('Deriving state...');
      return {
        configurationId: nextProps.p3Campaign.p3_configuration_id,
        hasSavedConfiguration: true
      };
    }
    return null;
  }

  render() {
    const {
      rankPoints,
      coverageAreas,
      previewAds,
      configurationId,
      templateOptionId,
      configurationName,
      showConfirmRemoveModal,
      confirmModalDiv,
      hasSavedConfiguration
    } = this.state;

    const { p3Configurations } = this.props;

    return (
      <div>
        {showConfirmRemoveModal ? (
          <ConfirmModal
            onCancel={this.toggleConfirmRemoveModal}
            onConfirm={this.onRemoveConfig}
            isModalOpen={showConfirmRemoveModal}
            element={confirmModalDiv}
            confirmText="Remove Configuration"
            description="You can't undo this action. Are you sure you want to remove this configuration?"
          />
        ) : null}
        <P3CampaignEditForm
          key={this.props.p3Campaign.p3_configuration_id}
          templateOptionId={templateOptionId}
          configurationName={configurationName}
          rankPoints={rankPoints}
          coverageAreas={coverageAreas}
          previewAds={previewAds}
          templates={p3Configurations}
          hasConfiguration={hasSavedConfiguration}
          configurationId={configurationId}
          isNameEditable={this.isNameEditable()}
          areRankPointsEditable={this.areRankPointsEditable()}
          isCampaignEditable={this.isCampaignEditable()}
          isReadOnlyConfiguration={readOnlyConfiguration(
            this.props.campaignStatusId,
            this.props.startDate,
            this.props.productFulfillment ? this.props.productFulfillment.opportunity_id : '',
            this.props.productFulfillment ? this.props.productFulfillment.order_source : '',
          )}
          onTemplateOptionChange={this.onTemplateOptionChange}
          onTemplateSelected={this.applyTemplate}
          onRankPointsChange={this.onRankPointsChange}
          onConfigurationNameChange={this.onConfigurationNameChange}
          onClickRankButton={this.onClickRankButton}
          onClickRemoveConfig={this.toggleConfirmRemoveModal}
        />
      </div>
    );
  }
}

export default P3CampaignEdit;
